import type {
    PersonalScheduleList,
    PersonalScheduleProgramHistory,
    Program
} from '@/domain'
import { isExerciseItem } from '@/domain'
import composeId from '@/lib/api/local'

const exerciseReferencePrefix = 'v2e:'

function decodeExerciseReference(
    referenceId: string
): { scheduleDayId: string; exerciseId: string } | undefined {
    if (!referenceId.startsWith(exerciseReferencePrefix)) return undefined
    const dayLengthEnd = referenceId.indexOf(
        ':',
        exerciseReferencePrefix.length
    )
    if (dayLengthEnd < 0) return undefined
    const dayLength = Number(
        referenceId.slice(exerciseReferencePrefix.length, dayLengthEnd)
    )
    if (!Number.isSafeInteger(dayLength) || dayLength < 0) return undefined
    const dayStart = dayLengthEnd + 1
    const dayEnd = dayStart + dayLength
    const exerciseLengthEnd = referenceId.indexOf(':', dayEnd)
    if (exerciseLengthEnd < 0) return undefined
    const exerciseLength = Number(referenceId.slice(dayEnd, exerciseLengthEnd))
    if (!Number.isSafeInteger(exerciseLength) || exerciseLength < 0) {
        return undefined
    }
    const exerciseStart = exerciseLengthEnd + 1
    const exerciseEnd = exerciseStart + exerciseLength
    if (exerciseEnd !== referenceId.length) return undefined
    return {
        scheduleDayId: referenceId.slice(dayStart, dayEnd),
        exerciseId: referenceId.slice(exerciseStart, exerciseEnd)
    }
}

export function composeExerciseReference(
    scheduleDayId: string,
    exerciseId: string
): string {
    return `${exerciseReferencePrefix}${scheduleDayId.length}:${scheduleDayId}${exerciseId.length}:${exerciseId}`
}

export function isExerciseReference(
    referenceId: string,
    scheduleDayId: string,
    exerciseId: string
): boolean {
    return (
        referenceId === composeExerciseReference(scheduleDayId, exerciseId) ||
        referenceId === composeId(scheduleDayId, exerciseId)
    )
}

export function resolveExerciseReference(
    referenceId: string,
    programme: Program,
    schedule: PersonalScheduleList[]
): { scheduleDayId: string; exerciseId: string } | undefined {
    const matches: Array<{ scheduleDayId: string; exerciseId: string }> = []
    for (const day of schedule) {
        const lesson = (programme.lessons || []).find(
            (candidate) => candidate.id === day.lessonId
        )
        if (!lesson) continue
        for (const item of lesson.items || []) {
            if (!isExerciseItem(item) || item.kind === 'timeout') continue
            if (isExerciseReference(referenceId, day.id, item.id)) {
                matches.push({ scheduleDayId: day.id, exerciseId: item.id })
            }
        }
    }
    return matches.length === 1 ? matches[0] : undefined
}

/** The reflection (self-evaluation) entry recorded for a schedule day. */
export function isDaySelfEvaluation(
    entry: PersonalScheduleProgramHistory,
    scheduleDayId: string
): boolean {
    return (
        entry.kind === 'programme.schedule.selfevaluation' &&
        entry.referenceId === scheduleDayId
    )
}

export function referenceBelongsToScheduleDay(
    referenceId: string,
    scheduleDayId: string,
    allScheduleDayIds: string[]
): boolean {
    if (referenceId === scheduleDayId) return true
    const decoded = decodeExerciseReference(referenceId)
    if (decoded) return decoded.scheduleDayId === scheduleDayId
    const legacyMatches = allScheduleDayIds.filter((dayId) =>
        referenceId.startsWith(`${dayId}.`)
    )
    return legacyMatches.length === 1 && legacyMatches[0] === scheduleDayId
}

/** Conservative ownership check for retention paths: ambiguous legacy
 * references may belong to every matching day and must not be orphaned. */
export function referenceMayBelongToScheduleDay(
    referenceId: string,
    scheduleDayId: string
): boolean {
    if (referenceId === scheduleDayId) return true
    const decoded = decodeExerciseReference(referenceId)
    if (decoded) return decoded.scheduleDayId === scheduleDayId
    return referenceId.startsWith(`${scheduleDayId}.`)
}

/** Some entry may belong to the schedule day (see referenceMayBelongToScheduleDay). */
export function hasHistoryForScheduleDay(
    scheduleDayId: string,
    entries: { referenceId: string }[]
): boolean {
    return entries.some((entry) =>
        referenceMayBelongToScheduleDay(entry.referenceId, scheduleDayId)
    )
}

export function replaceScheduleDayInReference(
    referenceId: string,
    oldScheduleDayId: string,
    newScheduleDayId: string
): string | undefined {
    if (referenceId === oldScheduleDayId) return newScheduleDayId
    const decoded = decodeExerciseReference(referenceId)
    if (decoded?.scheduleDayId === oldScheduleDayId) {
        return composeExerciseReference(newScheduleDayId, decoded.exerciseId)
    }
    if (referenceId.startsWith(`${oldScheduleDayId}.`)) {
        return `${newScheduleDayId}${referenceId.slice(oldScheduleDayId.length)}`
    }
    return undefined
}
